/**
 * WebUSB Adapter
 *
 * Uses the WebUSB API for connecting to USB thermal printers.
 * Works in Chrome and Edge on desktop.
 */

import type { PrinterAdapter, PrinterDevice } from '../types'

const PRINTER_CLASS_CODE = 0x07
const DEFAULT_CHUNK_SIZE = 64

type UsbEndpointInfo = {
  interfaceNumber: number
  endpointNumber: number
}

export class WebUsbAdapter implements PrinterAdapter {
  private device: USBDevice | null = null
  private endpoint: UsbEndpointInfo | null = null
  private connectedDevice: PrinterDevice | null = null

  /**
   * Check if WebUSB is supported
   */
  static isSupported(): boolean {
    return typeof navigator !== 'undefined' && 'usb' in navigator
  }

  /**
   * Scan for available USB printers
   * Note: WebUSB requires a user gesture to request a device.
   */
  async scan(): Promise<PrinterDevice[]> {
    if (!WebUsbAdapter.isSupported()) {
      console.warn('[WebUSB] WebUSB not supported')
      return []
    }

    try {
      const usb = navigator.usb
      if (!usb) {
        console.warn('[WebUSB] WebUSB not supported')
        return []
      }
      const device = await usb.requestDevice({
        filters: [{ classCode: PRINTER_CLASS_CODE }],
      })

      if (device) {
        this.device = device
        return [{
          id: this.getDeviceId(device),
          name: device.productName || 'USB Printer',
          type: 'usb',
          connected: device.opened || false,
        }]
      }

      return []
    } catch (error) {
      if ((error as Error).name === 'NotFoundError') {
        console.log('[WebUSB] User cancelled device selection')
        return []
      }
      console.error('[WebUSB] Scan error:', error)
      throw error
    }
  }

  /**
   * Connect to a printer by device ID
   */
  async connect(deviceId: string): Promise<boolean> {
    if (!WebUsbAdapter.isSupported()) {
      throw new Error('WebUSB not supported')
    }

    try {
      if (!this.device || this.getDeviceId(this.device) !== deviceId) {
        const usb = navigator.usb
        if (!usb) {
          throw new Error('WebUSB not supported')
        }
        const device = await usb.requestDevice({
          filters: [{ classCode: PRINTER_CLASS_CODE }],
        })
        if (this.getDeviceId(device) !== deviceId) {
          throw new Error('Selected device does not match requested device')
        }
        this.device = device
      }

      const device = this.device
      if (!device) {
        throw new Error('No USB device selected')
      }

      await device.open()
      if (!device.configuration) {
        await device.selectConfiguration(1)
      }

      const endpoint = this.findOutEndpoint(device)
      if (!endpoint) {
        throw new Error('No writable USB endpoint found')
      }

      await device.claimInterface(endpoint.interfaceNumber)

      this.endpoint = endpoint
      this.connectedDevice = {
        id: this.getDeviceId(device),
        name: device.productName || 'USB Printer',
        type: 'usb',
        connected: true,
      }

      console.log('[WebUSB] Connected to:', device.productName)
      return true
    } catch (error) {
      console.error('[WebUSB] Connect error:', error)
      await this.disconnect()
      return false
    }
  }

  async disconnect(): Promise<void> {
    if (this.device?.opened) {
      if (this.endpoint) {
        await this.device.releaseInterface(this.endpoint.interfaceNumber).catch(() => undefined)
      }
      await this.device.close().catch(() => undefined)
    }

    this.device = null
    this.endpoint = null
    this.connectedDevice = null
  }

  async isConnected(): Promise<boolean> {
    return this.device?.opened || false
  }

  getConnectedDevice(): PrinterDevice | null {
    if (this.connectedDevice && this.device?.opened) {
      return { ...this.connectedDevice, connected: true }
    }
    return null
  }

  async print(data: Uint8Array): Promise<boolean> {
    if (!this.device || !this.endpoint || !this.device.opened) {
      throw new Error('Not connected to a USB printer')
    }

    try {
      for (let i = 0; i < data.length; i += DEFAULT_CHUNK_SIZE) {
        const chunk = data.slice(i, i + DEFAULT_CHUNK_SIZE)
        await this.device.transferOut(this.endpoint.endpointNumber, chunk)
      }

      console.log('[WebUSB] Print successful, sent', data.length, 'bytes')
      return true
    } catch (error) {
      console.error('[WebUSB] Print error:', error)
      return false
    }
  }

  private getDeviceId(device: USBDevice): string {
    return device.serialNumber || `${device.vendorId}:${device.productId}`
  }

  private findOutEndpoint(device: USBDevice): UsbEndpointInfo | null {
    const configuration = device.configuration
    if (!configuration) return null

    for (const iface of configuration.interfaces) {
      for (const alternate of iface.alternates) {
        const outEndpoint = alternate.endpoints.find((endpoint: USBEndpoint) => endpoint.direction === 'out')
        if (outEndpoint) {
          return {
            interfaceNumber: iface.interfaceNumber,
            endpointNumber: outEndpoint.endpointNumber,
          }
        }
      }
    }

    return null
  }
}
